import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    NgZone,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { createGesture, Gesture, GestureDetail } from '@ionic/core';
import { Subscription } from 'rxjs';

import { PlacePanel } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { ProductOptView } from '@unpispas/upp-data';
import { ProductView } from '@unpispas/upp-data';
import { TicketProductView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

/**
 * PTF app cart host: POS mobile catalog (`upp-cart-select`) + ticket EDIT +
 * product options, with the same select↔ticket swipe as place cart.
 * Composed by `PtfPlaceShellComponent` (not inside `feature/pinpad`).
 */
@Component({
    selector: 'app-ptf-cart',
    templateUrl: './ptf-cart.html',
    styleUrl: './ptf-cart.scss',
})
export class PtfCartComponent implements AfterViewInit, OnDestroy {
    @Output() OnPanel = new EventEmitter<PlacePanel>();

    @ViewChild('swipeHost', { read: ElementRef }) swipeHost?: ElementRef<HTMLElement>;

    private _locked = false;
    private _ticket_loaded = false;
    private _ontab_subscription: Subscription | null = null;
    private _right_subscription: Subscription | null = null;
    private _cart_swipe_gesture: Gesture | null = null;
    private _cart_swipe_timeout: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private data: dataService,
        private change: ChangeDetectorRef,
        private zone: NgZone,
        public view: viewService,
    ) {}

    /*******************************/
    /* DISPLAY OPTIONS             */
    /*******************************/

    get place(): PlaceView | null {
        return this.data.alives.get(this.data.place) as PlaceView;
    }

    get ticket(): TicketView | null {
        const args = this.view.PanelArg;
        return (args && args['ticket']) ? (args['ticket'] as TicketView) : null;
    }

    get RightPanel(): string | null {
        return this.view.ScndTab;
    }

    get ShowCatalog(): boolean {
        return this.RightPanel !== 'ticket' && this.RightPanel !== 'product';
    }

    get ShowTicket(): boolean {
        return this.RightPanel === 'ticket';
    }

    get ShowProduct(): boolean {
        return this.RightPanel === 'product';
    }

    get TicketMode(): 'EDIT' | 'VIEW' {
        const args = this.view.PanelArg;
        return args && args['mode'] === 'VIEW' ? 'VIEW' : 'EDIT';
    }

    /** One-shot footer action from `upp-cart-select` (send / cash / card). */
    get TicketAction(): 'send' | 'cash' | 'card' | null {
        const args = this.view.PanelArg;
        const action = args && args['action'];
        if (action === 'send' || action === 'cash' || action === 'card') {
            return action;
        }
        return null;
    }

    /**
     * Sticky mount for `upp-ticket-edit`: set on first visit to the ticket panel;
     * clear when the in-progress ticket leaves `PanelArg` (or on desktop).
     */
    get TicketLoaded(): boolean {
        return !this.view.Desktop && this._ticket_loaded;
    }

    get IsLocked(): boolean {
        return this._locked;
    }

    set IsLocked(value: boolean) {
        if (this._locked === value) {
            return;
        }
        this._locked = value;
        this.change.markForCheck();
    }

    get PanelProduct(): ProductView | null {
        const args = this.view.PanelArg;
        return (args && args['product']) ? (args['product'] as ProductView) : null;
    }

    get PanelOptions(): Array<ProductOptView> | null {
        const args = this.view.PanelArg;
        return (args && args['options']) ? (args['options'] as Array<ProductOptView>) : null;
    }

    get PanelTicketProduct(): TicketProductView | null {
        const args = this.view.PanelArg;
        return (args && args['ticketproduct'])
            ? (args['ticketproduct'] as TicketProductView)
            : null;
    }

    /*******************************/
    /* LIFECYCLE                   */
    /*******************************/

    ngAfterViewInit(): void {
        this.change.markForCheck();

        // ScndTab updates emit OnTabChanged (not OnRightChanged) — same as place cart.
        this._ontab_subscription = this.view.OnTabChanged.subscribe(() => {
            this.change.markForCheck();
            this.SliderSetup();
        });

        this._right_subscription = this.view.OnRightChanged.subscribe(() => {
            this.change.markForCheck();
            if (!this.view.Desktop) {
                this.SliderSetup();
            }
            else {
                this.SetTicketLoaded();
            }
        });

        this.SliderSetup();
    }

    ngOnDestroy(): void {
        if (this._cart_swipe_timeout != null) {
            clearTimeout(this._cart_swipe_timeout);
            this._cart_swipe_timeout = null;
        }

        if (this._ontab_subscription) {
            this._ontab_subscription.unsubscribe();
            this._ontab_subscription = null;
        }

        if (this._right_subscription) {
            this._right_subscription.unsubscribe();
            this._right_subscription = null;
        }

        if (this._cart_swipe_gesture) {
            this._cart_swipe_gesture.destroy();
            this._cart_swipe_gesture = null;
        }
    }

    /*******************************/
    /* MOBILE SWIPE GESTURE        */
    /*******************************/

    /**
     * Ionic-style horizontal swipe between catalog and ticket edit (mobile only).
     * Same `createGesture` host pattern as place cart.
     */
    private SliderSetup(): void {
        this.SetTicketLoaded();

        if (this._cart_swipe_timeout != null) {
            clearTimeout(this._cart_swipe_timeout);
            this._cart_swipe_timeout = null;
        }

        this._cart_swipe_timeout = setTimeout(() => {
            this._cart_swipe_timeout = null;

            if (this._cart_swipe_gesture) {
                this._cart_swipe_gesture.destroy();
                this._cart_swipe_gesture = null;
            }

            if (this.view.Desktop) {
                return;
            }

            const el = this.swipeHost?.nativeElement;
            if (!el || !this.ticket) {
                return;
            }

            const swipeCommitMinPx = 36;
            const swipeVelocityTrigger = 0.28;

            this._cart_swipe_gesture = createGesture({
                el,
                gestureName: 'ptfcart-select-ticket-swipe',
                threshold: 15,
                direction: 'x',
                maxAngle: 38,
                canStart: () => this.CanSwipeStart(),
                onEnd: (detail: GestureDetail) => {
                    const { deltaX, velocityX } = detail;
                    let direction: 'next' | 'prev' | null = null;

                    if (deltaX <= -swipeCommitMinPx || velocityX <= -swipeVelocityTrigger) {
                        direction = 'next';
                    }
                    else if (deltaX >= swipeCommitMinPx || velocityX >= swipeVelocityTrigger) {
                        direction = 'prev';
                    }

                    if (direction === null) {
                        return;
                    }

                    this.zone.run(() => {
                        this.OnSlide(direction);
                    });
                },
            });

            this._cart_swipe_gesture.enable(true);
        }, 0);

        this.change.markForCheck();
    }

    private SetTicketLoaded(): void {
        if (this.view.Desktop || !this.ticket) {
            this._ticket_loaded = false;
            this.IsLocked = false;
            return;
        }

        if (this.RightPanel === 'ticket') {
            this._ticket_loaded = true;
        }
    }

    /** Swipe is off while product config is open, cart is locked, or there is no ticket. */
    CanSwipeStart(): boolean {
        if (this.view.Desktop || this.RightPanel === 'product' || this.IsLocked || !this.ticket) {
            return false;
        }
        return true;
    }

    /**
     * Cycles select (0) ↔ ticket (1). Next = left swipe, prev = right swipe.
     */
    OnSlide(direction: 'next' | 'prev'): void {
        if (!this.ticket) {
            return;
        }

        if (this.IsLocked && direction === 'prev' && this.RightPanel === 'ticket') {
            return;
        }

        const idx = this.RightPanel === 'ticket' ? 1 : 0;
        const nextIndex = direction === 'next' ? (idx + 1) % 2 : (idx - 1 + 2) % 2;

        if (nextIndex === 1) {
            this.OnPanel.emit({
                scnd: 'ticket',
                args: {
                    ticket: this.ticket,
                    mode: this.TicketMode,
                    action: this.TicketAction,
                },
                force: true,
            });
        }
        else {
            this.OnPanel.emit({
                main: 'cart',
                scnd: null,
                force: true,
            });
        }
    }

    /*******************************/
    /* EVENT HANDLERS              */
    /*******************************/

    /** Forwards child panels; adds `main: 'cart'` when opening product options without main. */
    OnChildPanel(panel: PlacePanel): void {
        if (panel.scnd === 'product' && !('main' in panel)) {
            this.OnPanel.emit({ ...panel, main: 'cart' });
            return;
        }

        this.OnPanel.emit(panel);
    }
}
